/**
 * Payment hooks.
 *
 * The important one is `usePaymentStatusPolling`, which resolves the
 * webhook-vs-redirect race: after Stripe confirms the charge in the browser,
 * our database may not know for another second or two, because activation
 * happens when Stripe's server-to-server webhook arrives. The UI therefore
 * asks the server rather than trusting the redirect.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { paymentsApi } from "@/services/paymentsApi";
import type {
  MyPaymentsResponse,
  PaymentPurpose,
  PaymentStatusResult,
  Quote,
} from "@/types/payments";

/** Publishable key + kill switch. Rarely changes, so cache it hard. */
export const usePaymentConfig = () =>
  useQuery({
    queryKey: ["payment-config"],
    queryFn: paymentsApi.getConfig,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

/**
 * Live order summary.
 *
 * `couponCode` is part of the query key, so typing a code refetches and the
 * summary updates. The call reserves nothing, so this is safe to run freely.
 */
export const useQuote = (
  purpose: PaymentPurpose,
  programId?: number | null,
  couponCode?: string | null,
  enabled = true,
  sponsorshipCode?: string | null,
) =>
  useQuery<Quote>({
    queryKey: [
      "payment-quote",
      purpose,
      programId ?? null,
      couponCode ?? null,
      sponsorshipCode ?? null,
    ],
    queryFn: () => paymentsApi.getQuote(purpose, programId, couponCode, sponsorshipCode),
    enabled,
    // A stale price must never be charged; always refetch on mount.
    staleTime: 0,
    // ...but NOT on every window focus. With a coupon applied this endpoint
    // re-runs full coupon validation, so alt-tabbing away and back was firing
    // a fresh validation against the user's own in-flight reservation. The
    // price still cannot go stale: the mount refetch above covers navigation,
    // and the server re-prices authoritatively at create_intent regardless of
    // anything cached here.
    refetchOnWindowFocus: false,
    retry: false,
  });

/**
 * Pending payments + entitlements. Drives PaymentGuard and resume routing.
 *
 * Deliberately server-owned: an interrupted payment must be recoverable on a
 * different device, after logout, or after a tab closed mid-3DS. Nothing here
 * is inferred from local state.
 */
export const useMyPayments = (enabled = true) =>
  useQuery<MyPaymentsResponse>({
    queryKey: ["my-payments"],
    queryFn: paymentsApi.getMyPayments,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

/** Terminal states: polling stops here. */
const isTerminal = (status: string) =>
  ["SUCCEEDED", "FAILED", "CANCELED", "REFUNDED", "PARTIALLY_REFUNDED"].includes(status);

/**
 * Terminal states while a submission is still in flight.
 *
 * FAILED is deliberately ABSENT.
 *
 * Stripe leaves a declined PaymentIntent open and reusable -- that is how
 * retrying a card works, and our own form relies on it (the Element keeps the
 * same clientSecret). So one intent legitimately carries several
 * payment_intent.payment_failed events followed by payment_intent.succeeded.
 *
 * Treating FAILED as terminal stopped the poll on the PREVIOUS attempt's
 * residue and never asked again. Measured on a real payment: the last decline
 * landed at 15:00:17 and the success at 15:00:25 -- eight seconds later. The
 * poll had already quit, so the webhook granted the entitlement, created the
 * PAB, and nobody was watching: the user was enrolled while staring at an
 * error screen.
 *
 * SUCCEEDED/CANCELED/REFUNDED are still terminal -- none of those can be
 * walked forward by a retry.
 */
const isTerminalWhileConfirming = (status: string) =>
  ["SUCCEEDED", "CANCELED", "REFUNDED", "PARTIALLY_REFUNDED"].includes(status);

/**
 * Delay before the next poll, by attempt number.
 *
 * Deliberately UNBOUNDED: the schedule accelerates away from the submit and
 * then holds at 10s forever, for as long as the user is on the screen.
 *
 * WHY THERE IS NO TIMEOUT ANY MORE
 * --------------------------------
 * Every previous version tried to answer "has enough time passed to give up?"
 * -- 12s, then 20s, keyed off decline codes. That question has no correct
 * answer. Measured gaps between the last decline and the success on the SAME
 * intent were 8s, 10s and 24s in three real sessions; whatever constant is
 * chosen, the next session exceeds it, and when it does the poll stops while
 * the payment is still live. One user sat on a frozen screen for minutes with
 * a $7,500 charge that had already succeeded server-side.
 *
 * The frontend cannot know when a payment finishes. The server can, and it
 * says so authoritatively (payment status + entitlement). So this now polls
 * until the SERVER gives a real answer, and simply never gives up on its own.
 *
 * The cost is trivial -- one request per 10s while a human watches a payment
 * screen -- and the endpoint is cheap for a settled payment: the Stripe lookup
 * only happens while the row is still open (see the cooldown in payments.py).
 */
const pollDelayMs = (attempt: number): number => {
  if (attempt === 0) return 1000;
  if (attempt === 1) return 2000;
  if (attempt === 2) return 3000;
  if (attempt < 6) return 5000;
  return 10000;
};

export interface PaymentPollingState {
  status: PaymentStatusResult | null;
  /** True while we are still waiting for the webhook to land. */
  isPolling: boolean;
  /**
   * True when polling gave up before reaching a terminal state.
   *
   * This is NOT a failure. The charge already went through; our activation is
   * just running late. The UI must say "we're finishing setup", never
   * "payment failed" — telling a charged user it failed produces duplicate
   * payments and support tickets.
   */
  /**
   * @deprecated Always false. The poll no longer gives up (see pollDelayMs),
   * so there is no timeout state -- it resolves against the server or the user
   * leaves the screen. Retained so existing callers keep compiling; the "slow"
   * branches they guard are now unreachable and can be deleted.
   */
  timedOut: boolean;
  error: unknown;
  /**
   * Ask the server once, right now. Drives the "Check again" button on the
   * slow screen so a waiting user has something to do besides reload the page
   * (reloading mid-payment is how people end up paying twice).
   *
   * Safe to call at any time: it resolves to the fresh status and updates
   * `status`, but never restarts the automatic schedule.
   */
  recheck: () => Promise<PaymentStatusResult | null>;
  /** True while a `recheck()` is in flight, for the button's own spinner. */
  isRechecking: boolean;
}

/**
 * Background re-checks after the main poll gives up.
 *
 * The main schedule stops at ~32s, but a slow settlement can take minutes (one
 * real payment took 17). Rather than leaving the screen inert, keep asking —
 * but far more gently than the opening burst, because every request that finds
 * an open payment costs a Stripe lookup server-side.
 *
 * 4 attempts at 15s covers the first minute past timeout, which catches the
 * common "webhook was a bit late" case. After that the screen stops on its own
 * and leaves only the manual button: past a minute this is a real backend
 * delay, and the 5-minute settlement reconciler owns it from there.
 */
const SLOW_RECHECK_INTERVAL_MS = 15000;
const SLOW_RECHECK_MAX_ATTEMPTS = 4;



/**
 * Poll a payment until it reaches a terminal state.
 *
 * 8 requests over ~30s (see POLL_SCHEDULE_MS). Well inside the endpoint's
 * 120/min limit with room to spare, even if a user retries a declined card
 * several times in the same minute.
 */
export function usePaymentStatusPolling(
  externalId: string | null,
  enabled: boolean,
): PaymentPollingState {
  const [status, setStatus] = useState<PaymentStatusResult | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [isRechecking, setIsRechecking] = useState(false);

  // Refs so the polling loop never restarts due to state changes.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  useEffect(() => {
    if (!externalId || !enabled) return;

    cancelledRef.current = false;
    setIsPolling(true);
    setTimedOut(false);
    setError(null);

    let attempt = 0;

    const tick = async () => {
      if (cancelledRef.current) return;

      try {
        const result = await paymentsApi.getStatus(externalId);
        if (cancelledRef.current) return;

        setStatus(result);

        // Stop ONLY on a real, settled answer from the server.
        //
        // FAILED is deliberately not one. Stripe leaves a declined intent open
        // and retryable -- that is how retrying a card works and what our own
        // form relies on -- so one intent legitimately carries several
        // payment_failed events before a succeeded one. Treating FAILED as
        // final stopped the poll on the previous attempt's residue and never
        // asked again, leaving users enrolled but staring at an error, or
        // frozen on a screen that never resolved.
        //
        // SUCCEEDED / CANCELED / REFUNDED cannot be walked forward by a retry,
        // so those are the only states worth stopping on.
        if (isTerminalWhileConfirming(result.status)) {
          setIsPolling(false);
          return;
        }
      } catch (err) {
        if (cancelledRef.current) return;
        // Transient failures (a 429, a blip) must not abort the poll — the
        // charge may already have succeeded and we still need the answer.
        setError(err);
      }

      // Never stops on its own. See pollDelayMs: the schedule holds at 10s
      // indefinitely, so no timing gap can strand a user on a live payment.
      // Cleanup on unmount (below) is what ends it.
      const delay = pollDelayMs(attempt);
      attempt += 1;
      timerRef.current = setTimeout(tick, delay);
    };

    void tick();

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [externalId, enabled]);

  useEffect(() => stop, [stop]);

  /**
   * One immediate status read, independent of the automatic schedule.
   *
   * Deliberately does NOT set `error` on failure: this backs a user-facing
   * button, and a transient blip should leave the existing message intact
   * rather than replacing it with an error the user can do nothing about.
   */
  const recheck = useCallback(async (): Promise<PaymentStatusResult | null> => {
    if (!externalId) return null;
    setIsRechecking(true);
    try {
      const result = await paymentsApi.getStatus(externalId);
      setStatus(result);
      return result;
    } catch {
      return null;
    } finally {
      setIsRechecking(false);
    }
  }, [externalId]);

  // Gentle background re-checks once the main schedule has given up.
  //
  // Separate effect (not part of `tick`) so its cadence and stop condition stay
  // independent of the opening burst. Stops as soon as the payment reaches a
  // terminal state, or after SLOW_RECHECK_MAX_ATTEMPTS — from there the manual
  // button and the server-side reconciler take over.
  useEffect(() => {
    if (!timedOut || !externalId || !enabled) return;
    if (status && isTerminal(status.status)) return;

    let attempts = 0;
    let cancelled = false;

    const id = setInterval(() => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > SLOW_RECHECK_MAX_ATTEMPTS) {
        clearInterval(id);
        return;
      }
      void (async () => {
        const result = await recheck();
        if (!cancelled && result && isTerminal(result.status)) clearInterval(id);
      })();
    }, SLOW_RECHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // `status` is intentionally omitted: including it would tear down and
    // recreate the interval on every re-check, resetting the attempt counter
    // and turning a bounded 4-attempt loop into an unbounded one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, externalId, enabled, recheck]);

  return { status, isPolling, timedOut, error, recheck, isRechecking };
}

/**
 * Does this user still owe money for a given program (or their PAB)?
 *
 * Returns the pending payment if one exists, so callers can route straight to
 * its server-provided `resumeUrl`.
 *
 * `isResolved` matters more than it looks. Route guards must not act on a
 * half-loaded answer: if one guard redirects TO the payment step while the
 * step's own guard reads a transient "no payment owed" and redirects away,
 * the two bounce the user between routes forever. Callers should therefore
 * gate every redirect on `isResolved`, and treat an unresolved state as
 * "don't move".
 */
export function usePendingPaymentFor(
  purpose: PaymentPurpose,
  programId?: number | null,
  enabled = true,
) {
  const { data, isLoading, isError, isFetching, refetch } = useMyPayments(enabled);

  const pending =
    data?.pendingPayments.find(
      (p) => p.purpose === purpose && (programId == null || p.programId === programId),
    ) ?? null;

  const isEntitled = programId != null
    ? Boolean(
        data?.entitlements.some((e) => e.programId === programId && e.status === "ACTIVE"),
      )
    : false;

  // Only true once we hold a real server answer. `data` persists across
  // background refetches, so this stays true during revalidation rather than
  // flickering — which is precisely what would cause a redirect loop.
  const isResolved = !enabled ? true : Boolean(data) && !isError;

  // "Does this user owe money?" must NOT be derived from `pending`.
  //
  // A pending payment row only exists once checkout has begun, so using it to
  // gate the wizard's Payment step is circular: no row → step hidden → the
  // user can never reach the step → the row is never created. The server
  // answers the question directly via pabRequiresPayment, which is computed
  // from entitlement + company coverage and works before any payment exists.
  const requiresPayment =
    purpose === "PAB_ACTIVATION"
      ? Boolean(data?.pabRequiresPayment)
      : Boolean(pending) && !isEntitled;

  return {
    pending,
    isEntitled,
    /** Authoritative "this user owes money for this scope". */
    requiresPayment,
    isResolved,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

/**
 * Is this user already enrolled in the program with this PUBLIC external id?
 *
 * Exists because public program payloads carry only `externalId`, never the
 * numeric id -- so `usePendingPaymentFor` (which matches on programId) cannot
 * answer the question on any page rendered from a public preview.
 *
 * Without it, the Explore program page showed "Enroll in Program" to someone
 * who had already paid. Pressing it returned 409 "already a member", which
 * surfaced as "Enrollment failed" -- telling a user who WAS enrolled that
 * their enrolment had failed. That happened routinely after a slow payment:
 * the status poll gives up after ~32s, but a card needing several attempts can
 * take minutes to settle (one real case took 17), so the tab was long gone by
 * the time the webhook enrolled them.
 *
 * `isResolved` matters as much as the answer: rendering a button against a
 * not-yet-loaded entitlement list is what made a hard refresh "fix" it. Hold
 * the loading state until this is true rather than guessing.
 */
export function useIsEnrolledInProgram(externalId: string | null | undefined, enabled = true) {
  const { data, isLoading, isError } = useMyPayments(enabled && Boolean(externalId));

  const isEnrolled = Boolean(
    externalId &&
      data?.entitlements.some(
        (e) => e.programExternalId === externalId && e.status === "ACTIVE",
      ),
  );

  return {
    isEnrolled,
    /** True once a real server answer is in hand -- gate rendering on this. */
    isResolved: !enabled || !externalId ? true : Boolean(data) && !isError,
    isLoading,
  };
}
